import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Badge,
  Spinner,
  Center,
  Icon,
  Collapse,
  useToast,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FaMapMarkerAlt, FaTruck, FaTimes } from "react-icons/fa";
import { buildAddress } from "../../../utils/util";
import { GaraazTextComponent } from "@svan-garaaz/reactsharedlib";
import { getShipments } from "../service";
import ShipmentsTable from "./ShipmentsTable";
import { FcShipped } from "react-icons/fc";
import EngagementModeBadge from "../../../components/EngagementModeBadge";
import { SecondLevelEntity, Shipment } from "../types";

export interface SecondLevelCardProps {
  entity: SecondLevelEntity;
  expanded: boolean;
  onToggleExpansion: () => void;
  isLoading?: boolean;
  firstLevelId: string;
  grouping: "customer" | "supplier";
  shipmentStatusFilter: "undelivered" | "delivered" | "cancelled";
  filters?: {
    dispatchMode?: string | null;
    cptSlabLevels?: number[];
  };
}

export default function SecondLevelCard({
  entity,
  expanded,
  onToggleExpansion,
  isLoading = false,
  firstLevelId,
  grouping,
  shipmentStatusFilter,
  filters,
}: SecondLevelCardProps) {
  const toast = useToast();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);

  const fetchShipments = useCallback(async () => {
    if (!firstLevelId || !entity._id) return;

    setShipmentsLoading(true);
    try {
      const response = await getShipments(
        firstLevelId,
        entity._id,
        grouping,
        shipmentStatusFilter,
        filters
      );
      setShipments(response);
    } catch (error) {
      console.error("Error fetching shipments:", error);
      toast({
        title: "Error",
        description: "Failed to fetch shipments for this entity",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShipmentsLoading(false);
    }
  }, [
    firstLevelId,
    entity._id,
    grouping,
    shipmentStatusFilter,
    filters,
    toast,
  ]);

  useEffect(() => {
    setShipments([]);
  }, [entity._id, grouping, shipmentStatusFilter, filters]);

  useEffect(() => {
    if (expanded && shipments.length === 0) {
      fetchShipments();
    }
  }, [expanded, shipments.length, fetchShipments]);

  // Memoize the entity address string
  const entityAddressText = useMemo(() => {
    const address = entity.address;
    if (!address) return "";

    return buildAddress({
      firstLine: address.addressLine1 || "",
      secondLine: address.addressLine2 || "",
      city: address.city || "",
      state: address.state || "",
      pincode: "",
    });
  }, [entity]);

  const getEntityTypeLabel = () => {
    return grouping === "customer" ? "Supplier" : "Customer";
  };

  return (
    <>
      <Box
        bg="white"
        borderRadius="xl"
        shadow="0 2px 8px rgba(0, 0, 0, 0.06)"
        border="1px"
        borderColor="gray.100"
        overflow="hidden"
        transition="all 0.2s"
        _hover={{ shadow: "0 4px 16px rgba(0, 0, 0, 0.1)" }}
      >
        {/* Header Section */}
        <Box
          bg="linear-gradient(135deg, #f8f7ff 0%, #f3f1ff 100%)"
          p={4}
          position="relative"
          overflow="hidden"
          onClick={onToggleExpansion}
          cursor={"pointer"}
        >
          {/* Subtle Background Pattern */}
          <Box
            position="absolute"
            top="-60px"
            right="-60px"
            w="150px"
            h="150px"
            opacity={0.02}
            bg="linear-gradient(135deg, #64748b 0%, #475569 100%)"
            borderRadius="full"
            transform="translate(50px, -50px)"
          />

          <VStack align="stretch" spacing={4} position="relative" zIndex={1}>
            {/* Main Header */}
            <HStack justify="space-between" align="flex-start">
              <VStack align="start" spacing={2} flex={1}>
                <VStack align="start" spacing={1.5}>
                  <HStack spacing={2} align="center">
                    <Heading size="md" fontWeight="600" color="gray.800">
                      {entity.name}
                    </Heading>
                    <HStack spacing={2}>
                      <Badge
                        bg="white"
                        color="gray.700"
                        variant="subtle"
                        px={2}
                        py={0.5}
                        borderRadius="full"
                        fontWeight="600"
                        shadow="0 1px 3px rgba(0, 0, 0, 0.06)"
                        border="1px"
                        borderColor="gray.200"
                        fontSize="xs"
                        lineHeight={"normal"}
                        textTransform={"none"}
                      >
                        {entity.totalShipmentsCount} Shipments
                      </Badge>
                      {/* Engagement Mode Badge */}
                      {entity.engagementMode && (
                        <EngagementModeBadge
                          engagementMode={entity.engagementMode}
                          size="sm"
                        />
                      )}
                    </HStack>
                  </HStack>
                  {/* Entity Address */}
                  {entity.address && (
                    <HStack spacing={2} color="gray.600">
                      <Icon
                        as={FaMapMarkerAlt}
                        boxSize={2.5}
                        color="purple.500"
                      />
                      <GaraazTextComponent
                        header=""
                        content={entityAddressText}
                        maxNoOfLines={3}
                        contentStyle={{
                          width: "400px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "gray.700",
                          cursor: "pointer",
                        }}
                      />
                    </HStack>
                  )}
                </VStack>
              </VStack>

              {/* Stats and Action Buttons */}
              <HStack spacing={4} align="center">
                {/* Entity Stats */}
                <HStack spacing={4}>
                  {shipmentStatusFilter === "undelivered" ? (
                    <VStack spacing={1} align="center" width={"50px"}>
                      <HStack spacing={1.5} align="center">
                        <Icon as={FaTruck} color="blue.500" boxSize={3} />
                        <Text fontSize="xs" color="gray.600" fontWeight="600">
                          Undelivered
                        </Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="700" color="gray.800">
                        {entity.totalUndeliveredShipments}
                      </Text>
                    </VStack>
                  ) : shipmentStatusFilter === "delivered" ? (
                    <VStack spacing={1} align="center" width={"50px"}>
                      <HStack spacing={1.5} align="center">
                        <Icon as={FcShipped} color="green.500" boxSize={3.5} />
                        <Text fontSize="xs" color="gray.600" fontWeight="600">
                          Delivered
                        </Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="700" color="gray.800">
                        {entity.totalDeliveredShipments || 0}
                      </Text>
                    </VStack>
                  ) : (
                    <VStack spacing={1} align="center" width={"50px"}>
                      <HStack spacing={1.5} align="center">
                        <Box position="relative">
                          <Icon as={FaTruck} color="red.500" boxSize={3.5} />
                          <Icon
                            as={FaTimes}
                            color="red.500"
                            boxSize={1.5}
                            position="absolute"
                            top="38%"
                            left="30%"
                            transform="translate(-50%, -50%)"
                            bg="white"
                          />
                        </Box>
                        <Text fontSize="xs" color="gray.600" fontWeight="600">
                          Cancelled
                        </Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="700" color="gray.800">
                        {entity.cancelledShipmentsCount || 0}
                      </Text>
                    </VStack>
                  )}
                </HStack>

                {/* Action Button */}
                <IconButton
                  aria-label="Toggle expansion"
                  icon={
                    expanded ? (
                      <ChevronUpIcon boxSize={6} />
                    ) : (
                      <ChevronDownIcon boxSize={6} />
                    )
                  }
                  _hover={{
                    bg: "none",
                  }}
                  variant="ghost"
                  size="md"
                />
              </HStack>
            </HStack>

            {/* Loading State */}
            {isLoading && (
              <HStack
                spacing={2}
                justify="center"
                bg="white"
                p={3}
                borderRadius="lg"
                shadow="0 1px 4px rgba(0, 0, 0, 0.06)"
              >
                <Spinner size="sm" color="blue.500" />
                <Text fontSize="xs" color="gray.600" fontWeight="500">
                  Loading shipments...
                </Text>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Shipments Table */}
        <Collapse in={expanded}>
          <Box p={0}>
            {shipmentsLoading ? (
              <Center py={8}>
                <VStack spacing={3}>
                  <Spinner size="md" color="blue.500" />
                  <Text color="gray.600" fontSize="sm">
                    Loading shipments for {entity.name}...
                  </Text>
                </VStack>
              </Center>
            ) : shipments.length === 0 ? (
              <Center py={8}>
                <VStack spacing={2}>
                  <Box p={3} bg="gray.100" borderRadius="full">
                    <Icon as={FaTruck} boxSize={4} color="gray.400" />
                  </Box>
                  <Text color="gray.500" fontSize="sm">
                    No shipments found for this{" "}
                    {getEntityTypeLabel().toLowerCase()}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <Box overflow="hidden" borderRadius="lg">
                <Box
                  maxH="72"
                  overflowY="auto"
                  overflowX="hidden"
                  sx={{
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c1c1c1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#a8a8a8",
                    },
                  }}
                >
                  <ShipmentsTable shipments={shipments} />
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </>
  );
}
